import { useState } from 'react';
import DashboardCard, { EmptyState } from './DashboardCard';
import type { DashboardCensusSummary } from '../../types/dashboard';
import { useNavigate } from 'react-router-dom';
import { useGlobalStore } from '../../stores/globalStore';

interface Props {
  data: DashboardCensusSummary;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
}

export default function CensusSummaryCard({ data, ...dragProps }: Props) {
  const navigate = useNavigate();
  const plural = useGlobalStore((s) => s.residentPluralLabel);
  const [view, setView] = useState<'diet' | 'unit'>('diet');
  const total = data.totalResidents + data.totalGuests;

  return (
    <DashboardCard severity="info" icon="bi-bar-chart-line" title="Census Summary" {...dragProps}>
      {total === 0 ? (
        <EmptyState message="No census data" />
      ) : (
        <div className="census-card-content">
          {/* The three headline totals moved to the Now strip, which is on
              screen at all times. Repeating them here put the same 230 twice
              on one page. What is left is the part worth a full card: the
              split by diet type and by unit. */}
          <div className="census-breakdown-tabs">
            <button
              className={`census-tab ${view === 'diet' ? 'active' : ''}`}
              onClick={() => setView('diet')}
            >
              By Diet Type
            </button>
            <button
              className={`census-tab ${view === 'unit' ? 'active' : ''}`}
              onClick={() => setView('unit')}
            >
              By Unit
            </button>
          </div>
          <div className="census-breakdown-list">
            {/* Each row links to the residents it counts. The card could say
                "147 on REGULAR" and offer no way to see which 147 — the last
                place on this dashboard reporting a number with no route to
                the thing behind it. */}
            {(view === 'diet' ? data.dietTypeBreakdown : data.unitBreakdown).map((item, i) => (
              <button
                key={i}
                type="button"
                className="census-breakdown-row"
                onClick={() => navigate(
                  `/people?${view === 'diet' ? 'diet' : 'unit'}=${encodeURIComponent(item.name)}`,
                )}
                title={`Show the ${item.count} ${plural.toLowerCase()} on ${item.name}`}
              >
                <span className="census-breakdown-name">{item.name}</span>
                <span className="census-breakdown-bar">
                  <span
                    className="census-breakdown-fill"
                    style={{ width: `${Math.round((item.count / data.totalResidents) * 100)}%` }}
                  />
                </span>
                <span className="census-breakdown-count">{item.count}</span>
              </button>
            ))}
            {(view === 'diet' ? data.dietTypeBreakdown : data.unitBreakdown).length === 0 && (
              <div className="census-breakdown-empty">No data</div>
            )}
          </div>
        </div>
      )}
    </DashboardCard>
  );
}
